import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, Loader2, Search, Check } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ellieArtifactService } from '../../services/ellieArtifact.service';
import type { EllieArtifact } from '../../services/supportRequest.service';
import { RelativeTime } from './requestUi';

/**
 * Pick artifacts to attach to a message.
 *
 * Lists what the current user can already see — the backend refuses to attach an
 * artifact the poster can't read, so offering more here would only produce a 403
 * on send.
 */
export function AttachArtifactDialog({
  open,
  onOpenChange,
  onAttach,
  alreadyAttachedIds,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onAttach: (artifacts: EllieArtifact[]) => void;
  alreadyAttachedIds: string[];
}) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Record<string, EllieArtifact>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['artifacts-for-attach'],
    queryFn: () => ellieArtifactService.find({ pageSize: 100 }),
    enabled: open,
    staleTime: 60_000,
  });

  const artifacts = useMemo(() => {
    const all = (data?.records ?? []) as EllieArtifact[];
    const term = search.trim().toLowerCase();
    if (!term) return all;
    return all.filter(
      (a) =>
        a.name?.toLowerCase().includes(term) ||
        a.description?.toLowerCase().includes(term),
    );
  }, [data, search]);

  const selectedList = Object.values(selected);

  const reset = () => {
    setSearch('');
    setSelected({});
  };

  const toggle = (artifact: EllieArtifact) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (next[artifact.id]) delete next[artifact.id];
      else next[artifact.id] = artifact;
      return next;
    });

  const confirm = () => {
    onAttach(selectedList);
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Attach an artifact</DialogTitle>
          <DialogDescription>
            Anyone on this request will be able to read it, whether or not it was
            shared with them directly.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search artifacts"
              className="pl-8"
              autoFocus
            />
          </div>

          <div className="max-h-72 overflow-y-auto -mx-1 px-1">
            {isLoading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground text-sm">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Loading artifacts
              </div>
            ) : artifacts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-10">
                {search.trim() ? 'No artifacts match that search.' : 'No artifacts yet.'}
              </p>
            ) : (
              <ul className="space-y-1">
                {artifacts.map((a) => {
                  const isSelected = !!selected[a.id];
                  const already = alreadyAttachedIds.includes(a.id);
                  return (
                    <li key={a.id}>
                      <button
                        type="button"
                        disabled={already}
                        onClick={() => toggle(a)}
                        className={`w-full text-left rounded-md border px-3 py-2 transition-colors flex items-start gap-2 ${
                          already
                            ? 'opacity-50 cursor-not-allowed border-transparent'
                            : isSelected
                              ? 'border-primary bg-accent'
                              : 'border-transparent hover:bg-accent/60'
                        }`}
                      >
                        <FileText className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{a.name}</p>
                          {a.description && (
                            <p className="text-xs text-muted-foreground truncate">
                              {a.description}
                            </p>
                          )}
                          <p className="text-[11px] text-muted-foreground">
                            {already ? (
                              'Already attached'
                            ) : (
                              <RelativeTime iso={a.created_at} />
                            )}
                          </p>
                        </div>
                        {isSelected && (
                          <Check className="h-4 w-4 flex-shrink-0 text-primary" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={confirm} disabled={selectedList.length === 0}>
            Attach{selectedList.length > 0 ? ` ${selectedList.length}` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
